/**
 * @fileoverview Datepicker custom (substitui o <input type="date"> nativo, que
 * abre o calendário feio do navegador e não combina com o tema). Mantém o valor
 * em "YYYY-MM-DD" (mesmo formato do input nativo) pra não quebrar nada.
 *
 * Props: { value: string, onChange: (isoOrEmpty) => void, placeholder?: string }
 */

import React, { useState, useEffect } from "react";
import { useTheme } from "@material-ui/core/styles";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";

const MONTHS_PT = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const WEEKDAYS_PT = ["D", "S", "T", "Q", "Q", "S", "S"];
const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (dt) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
const parseISO = (s) => {
  const m = typeof s === "string" && s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const fmtBR = (s) => {
  const d = parseISO(s);
  return d ? `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}` : "";
};
const sameYMD = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const CustomDatePicker = ({ value, onChange, placeholder }) => {
  const isDark = useTheme().palette.type === "dark";
  const [open, setOpen] = useState(false);
  const selDate = parseISO(value);
  const today = new Date();
  const [view, setView] = useState(() => {
    const d = parseISO(value) || new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  useEffect(() => {
    if (!open) return;
    const d = parseISO(value) || new Date();
    setView({ y: d.getFullYear(), m: d.getMonth() });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const T = (a) => (isDark ? `rgba(255,255,255,${a})` : `rgba(20,22,30,${a})`);
  const accent = "#4f7cff";
  const panelBg = isDark ? "#1e2230" : "#ffffff";
  const border = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";

  const gridStart = new Date(view.y, view.m, 1);
  gridStart.setDate(1 - gridStart.getDay());
  const cells = [];
  for (let i = 0; i < 42; i++) {
    cells.push(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i));
  }
  const goMonth = (delta) => {
    let m = view.m + delta;
    let y = view.y;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setView({ y, m });
  };
  const pick = (dt) => { onChange(toISO(dt)); setOpen(false); };

  return (
    <div style={{ position: "relative", marginTop: 6 }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
          width: "100%", boxSizing: "border-box", padding: "8px 12px", borderRadius: 8,
          border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.18)"}`,
          background: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
          color: isDark ? "#f0f2f5" : "#111318", fontSize: 12,
        }}
      >
        <Calendar size={14} style={{ flexShrink: 0, opacity: 0.7 }} />
        <span style={{ flex: 1, color: selDate ? "inherit" : T(0.4) }}>
          {selDate ? fmtBR(value) : (placeholder || "dd/mm/aaaa")}
        </span>
      </div>
      {open && (
        <React.Fragment>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 40 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 50, width: 240,
            background: panelBg, border: `1px solid ${border}`, borderRadius: 10,
            boxShadow: "0 8px 28px rgba(0,0,0,0.35)", padding: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T(0.85), textTransform: "capitalize" }}>
                {MONTHS_PT[view.m]} de {view.y}
              </div>
              <div style={{ display: "flex", gap: 2 }}>
                <span onClick={() => goMonth(-1)} title="Mês anterior" style={{ cursor: "pointer", padding: 4, color: T(0.6), display: "inline-flex" }}><ChevronLeft size={16} /></span>
                <span onClick={() => goMonth(1)} title="Próximo mês" style={{ cursor: "pointer", padding: 4, color: T(0.6), display: "inline-flex" }}><ChevronRight size={16} /></span>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 2 }}>
              {WEEKDAYS_PT.map((w, i) => (
                <div key={i} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: T(0.4), padding: "2px 0" }}>{w}</div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
              {cells.map((dt, i) => {
                const inMonth = dt.getMonth() === view.m;
                const isToday = sameYMD(dt, today);
                const isSel = sameYMD(dt, selDate);
                return (
                  <div
                    key={i}
                    onClick={() => pick(dt)}
                    onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = T(0.08); }}
                    onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = "transparent"; }}
                    style={{
                      height: 28, borderRadius: 6, cursor: "pointer", fontSize: 12,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: isSel ? "#fff" : (inMonth ? T(0.85) : T(0.28)),
                      fontWeight: isSel || isToday ? 700 : 400,
                      background: isSel ? accent : "transparent",
                      border: isToday && !isSel ? `1px solid ${accent}` : "1px solid transparent",
                    }}
                  >
                    {dt.getDate()}
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, paddingTop: 8, borderTop: `1px solid ${border}` }}>
              <span onClick={() => { onChange(""); setOpen(false); }} style={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: T(0.6) }}>Limpar</span>
              <span onClick={() => pick(new Date())} style={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: accent }}>Hoje</span>
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default CustomDatePicker;
